import { useState, type FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/db7ebpgmkl3spn8sm7s0';
type Status = 'idle' | 'sending' | 'success' | 'error';
const TOPICS = ['Strategy', 'Transformation', 'Operations'];

export default function Contact() {
  const [topic, setTopic] = useState('Strategy');
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [draft, setDraft] = useState('');
  const [count, setCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const trap = String(fd.get('company_alt') || '').trim();
    if (trap) { setStatus('success'); return; }
    fd.delete('company_alt');
    const name = String(fd.get('name') || '').trim();
    const email = String(fd.get('email') || '').trim();
    const message = String(fd.get('message') || '').trim();
    if (!name || !email || !message) { setStatus('error'); setFormError('Please complete name, work email and your message.'); return; }
    if (message.length > 500) { setStatus('error'); setFormError('Please keep your message under 500 characters.'); return; }

    const body = new URLSearchParams();
    fd.forEach((v, k) => body.append(k, String(v)));
    setDraft(`mailto:?subject=${encodeURIComponent(`Inquiry from ${name}`)}&body=${encodeURIComponent(`${message}\n\n${name}\n${email}\n${String(fd.get('company') || '')}`)}`);
    setStatus('sending');
    setFormError('');
    try {
      const response = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK' && !/spam/i.test(serverMsg || '')) {
        setStatus('success');
        form.reset();
        setCount(0);
        setTopic('Strategy');
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Your inquiry could not be sent.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error — your inquiry was not sent.');
    }
  };

  const input = 'w-full bg-transparent border-0 outline-none py-4 text-[15px] text-foreground-950 placeholder:text-foreground-400';
  const row = 'grid grid-cols-1 sm:grid-cols-[150px_1fr] items-baseline gap-x-6 border-b border-foreground-950/20 focus-within:border-foreground-950 transition-colors pt-3 sm:pt-0';

  return (
    <section id="contact" className="relative bg-background-50 sec overflow-hidden">
      <div className="absolute left-1/2 -translate-x-1/2 bottom-[-18%] w-[1100px] max-w-[160vw] h-[760px] contact-glow pointer-events-none" />
      <div className="relative wrap">
        <div className="text-center" data-reveal>
          <span className="label-xs text-foreground-600">Contact</span>
          <h2 className="font-heading font-light uppercase text-foreground-950 leading-[1] tracking-[0.02em] text-[38px] md:text-6xl lg:text-[76px] mt-6">Start a<br />conversation.</h2>
          <p className="mt-6 text-[15px] leading-[1.6] text-foreground-700 max-w-[420px] mx-auto">Tell us briefly what you are facing. A partner reads every inquiry.</p>
        </div>
        <form id="auren-inquiry" data-readdy-form onSubmit={handleSubmit} noValidate className="relative mx-auto w-full max-w-[680px] mt-14 md:mt-16" data-reveal>
          <input type="text" name="company_alt" className="inquiry-extra-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
          <input type="hidden" name="topic" value={topic} />
          <div className="grid grid-cols-3 gap-2 md:gap-3" role="group" aria-label="Area of interest">
            {TOPICS.map((t) => (
              <button key={t} type="button" aria-pressed={topic === t} onClick={() => setTopic(t)} className="btn-line text-foreground-950 !px-1 text-[10px] md:text-[11px]">{t}</button>
            ))}
          </div>
          <div className="mt-8">
            <label className={row}><span className="label-xs text-foreground-600">Name</span><input name="name" required className={input} placeholder="Your full name" /></label>
            <label className={row}><span className="label-xs text-foreground-600">Work email</span><input name="email" type="email" required className={input} placeholder="name@company.com" /></label>
            <label className={row}><span className="label-xs text-foreground-600">Company</span><input name="company" className={input} placeholder="Organization" /></label>
            <label className={row}>
              <span className="label-xs text-foreground-600 flex sm:flex-col justify-between gap-1">Message<span className="text-foreground-400">{count}/500</span></span>
              <textarea name="message" required maxLength={500} rows={3} onChange={(ev) => setCount(ev.target.value.length)} className={`${input} resize-none`} placeholder="A few lines about the challenge" />
            </label>
          </div>
          <div className="flex flex-col items-center gap-4 mt-12">
            <button type="submit" disabled={status === 'sending'} aria-label="Send an inquiry" className="group w-[120px] h-[120px] rounded-full border border-foreground-950 flex flex-col items-center justify-center gap-2 text-foreground-950 hover:bg-primary-500 hover:border-primary-500 hover:text-background-50 transition-colors duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60">
              <span className="label-xs !tracking-[0.18em] text-[10px]">{status === 'sending' ? 'Sending…' : 'Send an'}</span>
              <span className="label-xs !tracking-[0.18em] text-[10px]">{status === 'sending' ? '' : 'inquiry'}</span>
              <i className="ri-arrow-right-line text-lg transition-transform group-hover:translate-x-1" />
            </button>
            {status === 'success' && <p className="text-sm text-primary-500 text-center">Thank you &mdash; your inquiry has been received.</p>}
            {status === 'error' && (
              <div className="text-center text-sm text-foreground-800" role="alert">
                <p>{formError}</p>
                {draft && <a href={draft} className="link-line text-primary-500 cursor-pointer mt-1 inline-block">Open as an email draft instead</a>}
              </div>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}